import { ReactNode } from 'react';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { ConfigSummary } from '../data-qa-types';
import { formatRelativeTime, groupConfigsByModel, modelTypeForProductType } from '../data-qa-utils';
import { ModelCapsule } from './ModelCapsule';

interface ModelSwitcherProps {
  configs: ConfigSummary[];
  selectedConfigPath?: string;
  onSelect: (configPath: string) => void;
  // Shown at the right end of the band: the active tab's review controls, which apply
  // to the selected model.
  actions?: ReactNode;
}

// Multi-model workspace switcher. A company's configs (== its models, from
// `fetchConfigs(cid)`) group into one capsule per reviewable model (Brand
// Equity's two always-paired stage configs collapse into one — see
// groupConfigsByModel) in canonical model-type order; the active one is
// filled. Selecting a group always activates its primary (lowest
// brandEquityStage) config — where the two stages' data actually differs
// (e.g. Dependent Variable), that section renders its own stage tabs rather
// than needing a second top-level switcher control. A single-model company has nothing
// to switch, so its band holds only the review controls.
export const ModelSwitcher = ({
  configs,
  selectedConfigPath,
  onSelect,
  actions
}: ModelSwitcherProps) => {
  const groups = groupConfigsByModel(configs);
  // Grouped count, not raw configs.length -- a Brand-Equity-only company always has 2
  // configs (s1 + s2) collapsing into exactly 1 group, so the raw count is >= 2 even
  // though there's only one reviewable model to switch to/from (see the doc comment
  // above: "Hidden for single-model companies").
  if (groups.length <= 1) {
    if (!actions) return null;
    return (
      <div className='model-switcher model-switcher--actions-only'>
        <div className='model-switcher__actions'>{actions}</div>
      </div>
    );
  }
  return (
    <div className='model-switcher'>
      <div className='model-switcher__models'>
        <div className='model-switcher__label'>Model — showing data for</div>
        <div className='model-switcher__options'>
          {groups.map(group => {
            const activeConfig = group.configs.find(c => c.configPath === selectedConfigPath);
            const active = Boolean(activeConfig);
            const primary = activeConfig ?? group.configs[0];
            return (
              <InteractiveDiv
                key={group.key}
                className='model-switcher__option'
                aria-pressed={active}
                disabled={active}
                onClick={() => onSelect(primary.configPath)}
              >
                <ModelCapsule
                  modelType={modelTypeForProductType(group.productType)}
                  label={group.label}
                  active={active}
                />
                <span className='model-switcher__hint'>
                  {primary.lastApprovedAt
                    ? `Reviewed ${formatRelativeTime(primary.lastApprovedAt)}`
                    : 'Not yet reviewed'}
                </span>
              </InteractiveDiv>
            );
          })}
        </div>
      </div>
      {actions && <div className='model-switcher__actions'>{actions}</div>}
    </div>
  );
};
